import { useState } from 'react';
import { Sparkles, BarChart3, ChevronDown, ChevronUp, ImageDown } from 'lucide-react';
import ShareButton from '../components/ShareButton';
import ShareCardModal from '../components/ShareCardModal';
import { C } from '../constants';
import { formatNumber, safePct } from '../utils';
import Glass from '../components/Glass';
import StateResults from '../components/StateResults';
import DistrictMap from '../components/DistrictMap';
import DistrictBreakdown from '../components/DistrictBreakdown';

export default function PostVoteScreen({
  stateData,
  counts,
  userVote,
  votingClosed,
  highlightDistrict,
  setHighlightDistrict,
  showAll,
  setShowAll,
  error,
}) {
  const [showShareCard, setShowShareCard] = useState(false);
  const { partyById, districts, localFont, strings, accent } = stateData;
  const p = userVote ? partyById[userVote.party] : null;

  return (
    <main className="px-5 md:px-10 py-10 max-w-6xl mx-auto">

      {/* Thank-you ribbon — voter */}
      {userVote && p && (
        <Glass style={{
          padding:     '1rem 1.3rem',
          marginBottom:'2rem',
          border:      `1px solid ${p.color}55`,
          boxShadow:   `0 0 32px ${p.color}25`,
          animation:   'tnpoll-fadeup 600ms cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        }}>
          <div className="flex items-center gap-3 flex-wrap">
            <Sparkles size={20} color={p.color} style={{ filter: `drop-shadow(0 0 6px ${p.color})` }} />
            <div className="flex-1 min-w-0">
              <div style={{ fontWeight: 700, fontSize: '1.05rem', letterSpacing: '-0.005em', color: C.white }}>
                Thank you for voting. You are a responsible citizen.
              </div>
              <div style={{ fontFamily: localFont, fontSize: '0.86rem', color: C.textDim, marginTop: 2 }}>
                {strings.thankYou}
              </div>
            </div>
            {/* Share CTAs — right side of ribbon */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setShowShareCard(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: `rgba(0,229,255,0.10)`,
                  border: `1px solid ${C.cyan}55`,
                  padding: '7px 14px', borderRadius: 999,
                  fontSize: '0.78rem', fontWeight: 700, color: C.cyan,
                  cursor: 'pointer', fontFamily: "'Poppins', sans-serif",
                  whiteSpace: 'nowrap',
                }}
              >
                <ImageDown size={13} />
                Share Card
              </button>
              <ShareButton stateId={stateData.id} stateName={stateData.name} accent={accent} />
            </div>
          </div>
        </Glass>
      )}

      {/* Voting-closed banner — non-voter viewing published results */}
      {!userVote && votingClosed && (
        <Glass style={{
          padding:     '1rem 1.3rem',
          marginBottom:'2rem',
          border:      `1px solid ${accent}55`,
          boxShadow:   `0 0 32px ${accent}20`,
          animation:   'tnpoll-fadeup 600ms cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        }}>
          <div className="flex items-center gap-3 flex-wrap">
            <Sparkles size={20} color={accent} style={{ filter: `drop-shadow(0 0 6px ${accent})` }} />
            <div className="flex-1 min-w-0">
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: C.white }}>
                Voting is now closed. Results are live.
              </div>
              <div style={{ fontFamily: localFont, fontSize: '0.86rem', color: C.textDim, marginTop: 2 }}>
                {strings.votingClosed}
              </div>
            </div>
            {/* Share CTAs */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setShowShareCard(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: `rgba(0,229,255,0.10)`,
                  border: `1px solid ${C.cyan}55`,
                  padding: '7px 14px', borderRadius: 999,
                  fontSize: '0.78rem', fontWeight: 700, color: C.cyan,
                  cursor: 'pointer', fontFamily: "'Poppins', sans-serif",
                  whiteSpace: 'nowrap',
                }}
              >
                <ImageDown size={13} />
                Share Card
              </button>
              <ShareButton stateId={stateData.id} stateName={stateData.name} accent={accent} />
            </div>
          </div>
        </Glass>
      )}

      {error && (
        <Glass style={{ padding: '0.75rem 1rem', marginBottom: '1.5rem', border: `1px solid ${C.pink}55`, color: C.white, fontSize: '0.85rem' }}>
          {error}
        </Glass>
      )}

      <div style={{ fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: accent, fontWeight: 600 }}>
        Live results ·{' '}
        <span style={{ fontFamily: localFont, textTransform: 'none', letterSpacing: 0 }}>
          {strings.liveResults}
        </span>
      </div>
      <h2 style={{
        fontWeight: 700, fontSize: 'clamp(2rem, 5vw, 3.4rem)',
        lineHeight: 1.05, letterSpacing: '-0.025em',
        marginTop: '0.4rem', color: C.white,
      }}>
        How {stateData.name} is voting,
        <br />
        <span style={{
          background: `linear-gradient(90deg, ${accent}, ${C.pink})`,
          WebkitBackgroundClip: 'text', backgroundClip: 'text',
          color: 'transparent', fontStyle: 'italic',
        }}>
          right now.
        </span>
      </h2>

      <div
        className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-8"
        style={{ animation: 'tnpoll-fadeup 700ms 100ms both cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
      >
        <div className="lg:col-span-2">
          <StateResults counts={counts} stateData={stateData} />
        </div>
        <div className="lg:col-span-3">
          <DistrictMap
            counts={counts}
            userVote={userVote}
            highlight={highlightDistrict}
            onSelectDistrict={setHighlightDistrict}
            stateData={stateData}
          />
          <p style={{ fontSize: '0.78rem', color: C.textDim, marginTop: '0.6rem', fontStyle: 'italic' }}>
            Tap a tile to see that district's full breakdown below.
          </p>
        </div>
      </div>

      {/* District focus */}
      <div
        className="mt-10"
        style={{ animation: 'tnpoll-fadeup 700ms 250ms both cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
      >
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
          <div style={{ fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: accent, fontWeight: 600 }}>
            District focus ·{' '}
            <span style={{ fontFamily: localFont, textTransform: 'none', letterSpacing: 0 }}>
              {strings.districtFocus}
            </span>
          </div>
          <button
            onClick={() => setShowAll(s => !s)}
            className="inline-flex items-center gap-2"
            style={{
              background:           C.cardBg,
              border:               `1px solid ${C.border}`,
              padding:              '7px 14px',
              borderRadius:         999,
              fontWeight:           600,
              fontSize:             '0.82rem',
              color:                C.white,
              cursor:               'pointer',
              backdropFilter:       'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              fontFamily:           "'Poppins', sans-serif",
            }}
          >
            <BarChart3 size={14} />
            {showAll ? 'Hide all districts' : `See all ${districts.length} districts`}
            {showAll ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>

        {highlightDistrict && (
          <DistrictBreakdown
            districtName={highlightDistrict}
            counts={counts}
            isUserDistrict={userVote && userVote.district === highlightDistrict}
            stateData={stateData}
          />
        )}

        {showAll && (
          <Glass style={{ marginTop: '1.5rem', overflow: 'hidden', padding: 0 }}>
            <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 420 }}>
            <div
              className="grid grid-cols-12 px-4 py-2.5"
              style={{
                background: 'rgba(255,255,255,0.03)',
                fontSize: '0.62rem', letterSpacing: '0.2em', textTransform: 'uppercase',
                color: C.textDim, fontWeight: 600,
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <span className="col-span-5">District</span>
              <span className="col-span-3">Leading</span>
              <span className="col-span-2 text-right">Votes</span>
              <span className="col-span-2 text-right">Top %</span>
            </div>

            {districts.map(d => {
              const dCounts = counts[d.en] || {};
              const total   = Object.values(dCounts).reduce((a, b) => a + b, 0);
              let leader = null, lc = 0;
              Object.entries(dCounts).forEach(([pid, c]) => { if (c > lc) { leader = pid; lc = c; } });
              const lp     = leader ? partyById[leader] : null;
              const isUser = userVote && userVote.district === d.en;

              return (
                <button
                  key={d.en}
                  onClick={() => setHighlightDistrict(d.en)}
                  className="grid grid-cols-12 px-4 py-3 w-full text-left items-center"
                  style={{
                    borderTop:  `1px solid ${C.border}`,
                    background: highlightDistrict === d.en ? `${accent}0F` : 'transparent',
                    cursor:     'pointer',
                    color:      C.white,
                    transition: 'background 180ms ease',
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  <span className="col-span-5 flex items-baseline gap-2">
                    <span style={{ fontWeight: 700, fontSize: '0.92rem', color: C.white }}>{d.en}</span>
                    <span style={{ fontFamily: localFont, fontSize: '0.76rem', color: C.textDim }}>{d.local}</span>
                    {isUser && (
                      <span style={{
                        fontSize: '0.55rem', letterSpacing: '0.16em', textTransform: 'uppercase',
                        color: C.bg, background: `linear-gradient(135deg, ${accent}, ${C.pink})`,
                        padding: '2px 7px', fontWeight: 700, borderRadius: 999,
                      }}>
                        You
                      </span>
                    )}
                  </span>
                  <span className="col-span-3 flex items-center gap-2">
                    {lp ? (
                      <>
                        <span style={{ width: 10, height: 10, backgroundColor: lp.color, borderRadius: 2, boxShadow: `0 0 6px ${lp.color}AA`, display: 'inline-block', flexShrink: 0 }} />
                        <span style={{ fontWeight: 600, fontSize: '0.88rem', color: C.white }}>{lp.en}</span>
                      </>
                    ) : (
                      <span style={{ fontStyle: 'italic', color: C.textDimmer, fontSize: '0.85rem' }}>—</span>
                    )}
                  </span>
                  <span className="col-span-2 text-right" style={{ color: C.white, fontSize: '0.9rem', fontWeight: 600 }}>
                    {formatNumber(total)}
                  </span>
                  <span className="col-span-2 text-right" style={{ color: C.white, fontSize: '0.9rem', fontWeight: 600 }}>
                    {total > 0 ? `${safePct(lc, total).toFixed(0)}%` : '—'}
                  </span>
                </button>
              );
            })}
            </div>
            </div>
          </Glass>
        )}
      </div>

      {/* Bottom note */}
      <div className="mt-12 pt-6" style={{ borderTop: `1px solid ${C.border}` }}>
        <p style={{ fontStyle: 'italic', color: C.textDim, fontSize: '0.85rem' }}>
          Results refresh every few seconds.{' '}
          <span style={{ fontFamily: localFont, fontStyle: 'normal' }}>{strings.alreadyVoted}</span>
        </p>
      </div>

      {showShareCard && (
        <ShareCardModal
          stateData={stateData}
          counts={counts}
          userVote={userVote}
          onClose={() => setShowShareCard(false)}
        />
      )}
    </main>
  );
}
